import LoadingButton from '@mui/lab/LoadingButton';
import {
    Alert,
    alpha,
    Box,
    Button,
    Checkbox,
    DialogContent,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from '@mui/material';
import moment from 'moment';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { OnlyFansMassDMMessageScheduleDocumentWithColor } from '../../../../hooks/useMessageUsersMassDmList';
import { MassDmScheduleAdjustUnit } from '../../../../hooks/useMessageUsersMassDmScheduleList';
import DialogAdaptive from '../../../common/DialogAdaptive';
import StyledCardTitleBar from '../../../common/StyledCardTitleBar';
import StyledDialogActions from '../../../common/StyledDialogActions';

type Props = {
    scheduledMessages: OnlyFansMassDMMessageScheduleDocumentWithColor[];
    refetchMassDmsSchedule: () => void;
    bulkAdjustScheduledMessages: (
        scheduleIds: string[],
        offsetAmount: number,
        offsetUnit: MassDmScheduleAdjustUnit,
    ) => Promise<{ success: boolean; adjustedCount?: number }>;
    bulkAdjustScheduledMessagesLoading: boolean;
    open: boolean;
    setOpen: (open: boolean) => void;
};

type SortField = 'name' | 'active' | 'scheduleDate' | 'price';
type SortDirection = 'asc' | 'desc';
type AdjustDirection = 'later' | 'earlier';

const dateFormat = 'DD/MM/YYYY hh:mm A';

const MassageUsersAutoMassDmBulkAdjustDialog = (props: Props) => {
    const { scheduledMessages, refetchMassDmsSchedule, bulkAdjustScheduledMessages, bulkAdjustScheduledMessagesLoading, open, setOpen } =
        props;
    const { enqueueSnackbar } = useSnackbar();
    const dialog = useDialog();

    const [selectedScheduleIds, setSelectedScheduleIds] = useState<string[]>([]);
    const [direction, setDirection] = useState<AdjustDirection>('later');
    const [offsetAmount, setOffsetAmount] = useState<number>(1);
    const [offsetUnit, setOffsetUnit] = useState<MassDmScheduleAdjustUnit>('hours');

    // Sorting state
    const [sortField, setSortField] = useState<SortField>('scheduleDate');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    // Signed offset sent to the server: negative moves earlier
    const signedOffset = direction === 'later' ? offsetAmount : -offsetAmount;

    const shiftDate = (date: Date | string): moment.Moment => moment(date).add(signedOffset, offsetUnit);

    // Sort the messages based on current sort settings
    const sortedMessages = useMemo(() => {
        return [...scheduledMessages].sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (sortField) {
                case 'name':
                    aValue = a.name.toLowerCase();
                    bValue = b.name.toLowerCase();
                    break;
                case 'active':
                    aValue = a.active;
                    bValue = b.active;
                    break;
                case 'scheduleDate':
                    aValue = a.scheduleDate ? new Date(a.scheduleDate).getTime() : 0;
                    bValue = b.scheduleDate ? new Date(b.scheduleDate).getTime() : 0;
                    break;
                case 'price':
                    aValue = a.price ?? 0;
                    bValue = b.price ?? 0;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [scheduledMessages, sortField, sortDirection]);

    const selectedScheduledMessages = useMemo(
        () => scheduledMessages.filter(message => selectedScheduleIds.includes(message._id)),
        [scheduledMessages, selectedScheduleIds],
    );

    // Selected items whose new time would already be in the past (they would send at the next scheduler run)
    const now = moment();
    const movedIntoPastCount = selectedScheduledMessages.filter(
        message => message.scheduleDate && shiftDate(message.scheduleDate).isBefore(now),
    ).length;

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleClose = () => {
        // Reset selection and inputs when the dialog is closed
        setSelectedScheduleIds([]);
        setDirection('later');
        setOffsetAmount(1);
        setOffsetUnit('hours');
        setOpen(false);
    };

    const offsetDescription = `${offsetAmount.toLocaleString()} ${offsetAmount === 1 ? offsetUnit.slice(0, -1) : offsetUnit} ${direction}`;

    // Handle adjust action
    const handleAdjust = () => {
        if (offsetAmount < 1 || selectedScheduleIds.length === 0) {
            return;
        }

        dialog
            .confirm({
                title: 'Adjust Scheduled Mass DMs',
                message: `Move ${selectedScheduleIds.length.toLocaleString()} Scheduled Mass DMs ${offsetDescription}?${
                    movedIntoPastCount > 0
                        ? ` ${movedIntoPastCount.toLocaleString()} of them will end up in the past and will be sent at the next scheduler run.`
                        : ''
                }`,
                ok: {
                    text: 'Adjust',
                    color: 'primary',
                    variant: 'contained',
                },
                cancel: {
                    text: 'Cancel',
                    color: 'secondary',
                    variant: 'text',
                },
            })
            .then(() => {
                bulkAdjustScheduledMessages(selectedScheduleIds, signedOffset, offsetUnit)
                    .then(res => {
                        if (res.success) {
                            enqueueSnackbar(`Scheduled Mass DMs moved ${offsetDescription}`, {
                                variant: 'success',
                            });

                            refetchMassDmsSchedule();
                            handleClose();
                        } else {
                            enqueueSnackbar('Failed to adjust Scheduled Mass DMs', { variant: 'error' });
                        }
                    })
                    .catch(err => {
                        console.error(err);
                        enqueueSnackbar('Failed to adjust Scheduled Mass DMs', { variant: 'error' });
                    });
            })
            .catch(() => {
                // Handle cancel
            });
    };

    const sortLabelSx = {
        fontWeight: 'bold',
        '& .MuiTableSortLabel-icon': {
            color: 'inherit !important',
        },
    };

    return (
        <DialogAdaptive open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
            <StyledCardTitleBar title={`Scheduled Mass DMs Bulk Adjust`} />
            <DialogContent sx={{ pl: 0, pr: 0 }}>
                <Stack direction="column" spacing={2}>
                    <Typography variant="body1" color="text.secondary" pl={2} pr={2}>
                        Select the Scheduled Mass DMs whose scheduled time you want to move. Every selected Mass DM will be moved by the
                        same amount, keeping the gaps between them. Useful after the clocks change. You can select all Scheduled Mass DMs by
                        checking the checkbox in the header.
                    </Typography>

                    <Box sx={{ pl: 2, pr: 2 }}>
                        <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                                Move the selected Scheduled Mass DMs...
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <TextField
                                    label="By..."
                                    type="number"
                                    size="small"
                                    value={offsetAmount}
                                    onChange={e => setOffsetAmount(Math.max(1, parseInt(e.target.value, 10) || 0))}
                                    fullWidth
                                    InputProps={{
                                        inputProps: { min: 1 },
                                    }}
                                    error={offsetAmount < 1}
                                />

                                <Select
                                    value={offsetUnit}
                                    size="small"
                                    onChange={e => setOffsetUnit(e.target.value as MassDmScheduleAdjustUnit)}
                                    fullWidth
                                >
                                    <MenuItem value="minutes">Minutes</MenuItem>
                                    <MenuItem value="hours">Hours</MenuItem>
                                    <MenuItem value="days">Days</MenuItem>
                                    <MenuItem value="weeks">Weeks</MenuItem>
                                </Select>

                                <Select
                                    value={direction}
                                    size="small"
                                    onChange={e => setDirection(e.target.value as AdjustDirection)}
                                    fullWidth
                                >
                                    <MenuItem value="later">Later</MenuItem>
                                    <MenuItem value="earlier">Earlier</MenuItem>
                                </Select>
                            </Stack>
                        </Stack>
                    </Box>

                    <Typography variant="body2" color="text.secondary" pl={2} pr={2}>
                        {selectedScheduleIds.length.toLocaleString()} Scheduled Mass DMs selected
                    </Typography>

                    {movedIntoPastCount > 0 && (
                        <Box sx={{ pl: 2, pr: 2 }}>
                            <Alert severity="warning">
                                {movedIntoPastCount.toLocaleString()} selected Mass DMs would move into the past and will be sent at the
                                next scheduler run.
                            </Alert>
                        </Box>
                    )}

                    <Box
                        sx={{
                            width: '100%',
                            overflow: 'hidden',
                            backgroundColor: theme => alpha(theme.palette.primary.main, 0.1),
                        }}
                    >
                        <TableContainer
                            sx={{
                                maxHeight: 400,
                                // Reduce the padding between the cells
                                '& .MuiTableCell-root': {
                                    padding: '4px 8px',
                                },
                                // remove the left padding of the first cell
                                '& .MuiTableCell-root:first-of-type': {
                                    paddingLeft: 0,
                                },
                                // remove the right padding of the last cell
                                '& .MuiTableCell-root:last-of-type': {
                                    paddingRight: 2,
                                },
                            }}
                        >
                            <Table
                                size="small"
                                sx={{
                                    // Pin the header row
                                    '& thead th': {
                                        position: 'sticky',
                                        top: 0,
                                        backgroundColor: 'rgb(204, 223, 227)',
                                        zIndex: 1,
                                    },
                                    // Pin the first column
                                    '& th:first-of-type, & td:first-of-type': {
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 1,
                                    },
                                }}
                            >
                                <TableHead
                                    sx={{
                                        backgroundColor: 'rgb(204, 223, 227)',
                                        // pin the first column
                                        '& th:first-of-type': {
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 2,
                                        },
                                    }}
                                >
                                    <TableRow
                                        sx={{
                                            // every cell in the header row
                                            '& th': {
                                                backgroundColor: theme => theme.palette.background.paper,
                                                color: theme => theme.palette.text.primary,
                                                fontWeight: 'bold',
                                            },
                                        }}
                                    >
                                        <TableCell width={50} align="center">
                                            <Checkbox
                                                indeterminate={
                                                    selectedScheduleIds.length > 0 && selectedScheduleIds.length < sortedMessages.length
                                                }
                                                checked={sortedMessages.length > 0 && selectedScheduleIds.length === sortedMessages.length}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    if (e.target.checked) {
                                                        setSelectedScheduleIds(sortedMessages.map(message => message._id));
                                                    } else {
                                                        setSelectedScheduleIds([]);
                                                    }
                                                }}
                                                color="primary"
                                                size="small"
                                                sx={{
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        backgroundColor: 'transparent',
                                                    },
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={sortField === 'name'}
                                                direction={sortField === 'name' ? sortDirection : 'asc'}
                                                onClick={() => handleSort('name')}
                                                sx={sortLabelSx}
                                            >
                                                Mass DM Name
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center" width={80}>
                                            <TableSortLabel
                                                active={sortField === 'active'}
                                                direction={sortField === 'active' ? sortDirection : 'asc'}
                                                onClick={() => handleSort('active')}
                                                sx={sortLabelSx}
                                            >
                                                Active
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell width={150}>
                                            <TableSortLabel
                                                active={sortField === 'scheduleDate'}
                                                direction={sortField === 'scheduleDate' ? sortDirection : 'asc'}
                                                onClick={() => handleSort('scheduleDate')}
                                                sx={sortLabelSx}
                                            >
                                                Scheduled Date
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell width={150}>New Date</TableCell>
                                        <TableCell width={80} align="center">
                                            <TableSortLabel
                                                active={sortField === 'price'}
                                                direction={sortField === 'price' ? sortDirection : 'asc'}
                                                onClick={() => handleSort('price')}
                                                sx={sortLabelSx}
                                            >
                                                Price
                                            </TableSortLabel>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {sortedMessages.map(scheduledMessage => {
                                        const isSelected = selectedScheduleIds.includes(scheduledMessage._id);

                                        return (
                                            <TableRow
                                                key={scheduledMessage._id}
                                                sx={{
                                                    '&:last-child td, &:last-child th': {
                                                        border: 0,
                                                    },
                                                    backgroundColor: theme =>
                                                        isSelected ? alpha(theme.palette.primary.main, 0.2) : 'transparent',
                                                }}
                                            >
                                                <TableCell align="center">
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                            if (e.target.checked) {
                                                                setSelectedScheduleIds([...selectedScheduleIds, scheduledMessage._id]);
                                                            } else {
                                                                setSelectedScheduleIds(
                                                                    selectedScheduleIds.filter(id => id !== scheduledMessage._id),
                                                                );
                                                            }
                                                        }}
                                                        color="primary"
                                                        size="small"
                                                        sx={{
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                backgroundColor: 'transparent',
                                                            },
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.primary" noWrap>
                                                        {scheduledMessage.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography
                                                        variant="body2"
                                                        color={scheduledMessage.active ? 'success.main' : 'text.secondary'}
                                                        noWrap
                                                    >
                                                        {scheduledMessage.active ? 'Yes' : 'No'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary" noWrap>
                                                        {scheduledMessage.scheduleDate
                                                            ? moment(scheduledMessage.scheduleDate).format(dateFormat)
                                                            : 'Not scheduled'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        color={
                                                            isSelected && scheduledMessage.scheduleDate ? 'primary.main' : 'text.disabled'
                                                        }
                                                        fontWeight={isSelected && scheduledMessage.scheduleDate ? 'bold' : 'normal'}
                                                        noWrap
                                                    >
                                                        {scheduledMessage.scheduleDate
                                                            ? shiftDate(scheduledMessage.scheduleDate).format(dateFormat)
                                                            : '—'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2" color="text.secondary" noWrap>
                                                        {(scheduledMessage.type || 'manual') === 'template'
                                                            ? 'From template'
                                                            : scheduledMessage.price
                                                            ? `$${scheduledMessage.price.toFixed(2)}`
                                                            : 'Free'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Stack>
            </DialogContent>

            <StyledDialogActions>
                <Button onClick={handleClose} color="secondary" variant="text">
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    color="primary"
                    size="small"
                    loading={bulkAdjustScheduledMessagesLoading}
                    disabled={selectedScheduleIds.length === 0 || offsetAmount < 1}
                    onClick={() => {
                        handleAdjust();
                    }}
                >
                    <span>Adjust Selected</span>
                </LoadingButton>
            </StyledDialogActions>
        </DialogAdaptive>
    );
};

export default MassageUsersAutoMassDmBulkAdjustDialog;
